import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "react-hot-toast";
import { getHunarFunnelRecords, getHunarFunnelWhatsapp, retriggerHunarCancelledCalls, updateFunnelManualStatus, updateFunnelNote } from "../../services/hunarFunnelApi";
import { getHunarAllocations } from "../../services/hunarAllocationApi";
import { useRbac } from "../../context";
import { isAiFunnelOnlyUser } from "../../utils/aiFunnelUser";
import { SearchableSelect } from "../../components/ui/searchable-select";
import ApproachWhatsAppChatPopover from "../../components/approaches/ApproachWhatsAppChatPopover";

type FunnelRecord = {
  _id?: string;
  id?: string;
  candidateName: string;
  mobileNumber: string;
  email?: string | null;
  sheetJobId?: string | null;
  requestId: string;
  callId?: string | null;
  recordStatus: string;
  manualStatus?: string | null;
  manualStatusReason?: string | null;
  manualStatusUpdatedAt?: string | null;
  interestUpdatedAt?: string | null;
  latestEventType?: string | null;
  latestStatus?: string | null;
  latestLifecycleStatus?: string | null;
  answeredBy?: string | null;
  retryCount?: number | null;
  retriesLeft?: number | null;
  retryReason?: string | null;
  durationSeconds?: number | null;
  durationMinutes?: number | null;
  recordingUrl?: string | null;
  result?: Record<string, string> | null;
  hunarAgentId?: string | null;
  recruiter?: { username?: string; email?: string; role?: string } | null;
  allocatedRecruiterId?: { username?: string; email?: string; role?: string } | null;
  internalJobId?: { title?: string; location?: string; companyName?: string } | null;
  createdAt?: string;
  note?: string | null;
  source?: "google_sheet" | "api" | "approach" | null;
  interestedWhatsappSentAt?: string | null;
};

const OUTCOME_KEYS = [
  "Interest",
  "Salary Fit",
  "Shift Concern",
  "Age eligibility",
  "English Fluency",
  "Language Qualified",
  "Location Confirmed",
  "Communication Feedback",
  "Overall Recommendation",
  "Year Of Birth Qualified",
  "Overall Screening Quality",
];

const MANUAL_STATUS_LABELS: Record<string, string> = {
  not_interested: "Not Interested",
  interested: "Interested",
  scheduled: "Scheduled",
  not_reachable: "Not Reachable",
  call_back: "Call Back",
};

const SOURCE_LABELS: Record<string, string> = {
  approach: "Approaches",
  google_sheet: "Sheet",
  api: "API",
};

const formatUpdatedAt = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN");
};

const getTimeGapMinutes = (from?: string | null, to?: string | null) => {
  if (!from || !to) return null;
  const start = new Date(from).getTime();
  const end = new Date(to).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  return Math.abs(Math.round((end - start) / 60000));
};

const formatTimeGap = (from?: string | null, to?: string | null) => {
  const totalMinutes = getTimeGapMinutes(from, to);
  if (totalMinutes === null) return "";
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  return `${minutes}m`;
};

const responseTimeClass = (minutes: number | null) => {
  if (minutes === null) return "";
  if (minutes > 60) return "text-red-600 font-medium";
  if (minutes > 30) return "text-orange-500 font-medium";
  return "text-green-600 font-medium";
};

const formatDuration = (record: FunnelRecord) => {
  const durationSec = typeof record.durationSeconds === "number" ? record.durationSeconds : null;
  if (durationSec !== null) {
    return durationSec > 59
      ? `${Math.floor(durationSec / 60)}m ${Math.round(durationSec % 60)}s`
      : `${Math.round(durationSec)}s`;
  }
  return typeof record.durationMinutes === "number" ? `${record.durationMinutes}m` : "";
};

const toExcelRow = (record: FunnelRecord) => {
  const recruiter = record.recruiter || record.allocatedRecruiterId;
  const jobTitle = record.internalJobId?.title
    ? `${record.internalJobId.title}${record.internalJobId.location ? `, ${record.internalJobId.location}` : ""}`
    : "";
  const row: Record<string, string> = {
    Created: record.createdAt ? new Date(record.createdAt).toLocaleString("en-IN") : "",
    Candidate: record.candidateName || "",
    Mobile: record.mobileNumber || "",
    Source: SOURCE_LABELS[record.source || ""] || record.source || "",
    Status: MANUAL_STATUS_LABELS[record.manualStatus || ""] || record.manualStatus || "",
    "Sheet Job ID": record.sheetJobId || "",
    Duration: formatDuration(record),
    "Retry count":
      typeof record.retryCount === "number" && !Number.isNaN(record.retryCount)
        ? String(record.retryCount)
        : "",
    "Retries left":
      typeof record.retriesLeft === "number" && !Number.isNaN(record.retriesLeft)
        ? String(record.retriesLeft)
        : "",
    Recording: record.recordingUrl || "",
  };
  OUTCOME_KEYS.forEach((key) => {
    row[key] = record.result?.[key] || "";
  });
  row.Recruiter = recruiter?.username || recruiter?.email || "";
  row.Job = jobTitle;
  row.Company = record.internalJobId?.companyName || "";
  row.Note = record.note || record.manualStatusReason || "";
  return row;
};

const HunarFunnelPage: React.FC = () => {
  const { user } = useRbac();
  const [records, setRecords] = useState<FunnelRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    manualStatus: "",
    fromDate: "",
    toDate: "",
    interest: "",
    source: "",
    recruiterId: "",
  });
  const [recruiters, setRecruiters] = useState<Array<{ id: string; username: string; email: string }>>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const pageSize = 20;
  const [exporting, setExporting] = useState(false);
  const [retriggering, setRetriggering] = useState(false);
  const [form, setForm] = useState({
    candidateName: "",
    mobileNumber: "",
    email: "",
    sheetJobId: "",
    sourceRecordKey: "",
  });

  const [reasonModal, setReasonModal] = useState<{ recordId: string; open: boolean }>({ recordId: "", open: false });
  const [reasonText, setReasonText] = useState("");
  const [noteModal, setNoteModal] = useState<{ recordId: string; open: boolean }>({ recordId: "", open: false });
  const [noteText, setNoteText] = useState("");

  const handleManualStatusChange = async (record: FunnelRecord, value: string) => {
    const recordId = record._id || record.id || record.requestId;
    if (value === "not_interested") {
      setReasonModal({ recordId, open: true });
      setReasonText(record.note || record.manualStatusReason || "");
      return;
    }
    try {
      await updateFunnelManualStatus(recordId, value || null, null);
      await loadRecords(page);
    } catch { /* toast already shown */ }
  };

  const submitNotInterested = async () => {
    if (!reasonText.trim()) return;
    try {
      await updateFunnelManualStatus(reasonModal.recordId, "not_interested", reasonText.trim());
      setReasonModal({ recordId: "", open: false });
      setReasonText("");
      await loadRecords(page);
    } catch { /* toast already shown */ }
  };

  const openNoteModal = (record: FunnelRecord) => {
    setNoteModal({ recordId: record._id || record.id || record.requestId, open: true });
    setNoteText(record.note || record.manualStatusReason || "");
  };

  const submitNote = async () => {
    try {
      await updateFunnelNote(noteModal.recordId, noteText.trim() || null);
      setNoteModal({ recordId: "", open: false });
      setNoteText("");
      await loadRecords(page);
    } catch { /* toast already shown */ }
  };

  const loadRecords = async (p = page) => {
    setLoading(true);
    try {
      const response = await getHunarFunnelRecords({
        search: filters.search || undefined,
        status: filters.status || undefined,
        manualStatus: filters.manualStatus || undefined,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        interest: filters.interest || undefined,
        source: filters.source || undefined,
        recruiterId: filters.recruiterId || undefined,
        limit: pageSize,
        page: p,
      });
      setRecords(response?.data?.records || []);
      setTotalPages(response?.totalPages || 1);
      setTotalResults(response?.totalResults || 0);
      setPage(response?.page || p);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecords(1);
  }, [filters]);

  useEffect(() => {
    if (user?.role !== "ADMIN") return;
    (async () => {
      try {
        const response = await getHunarAllocations();
        const byId = new Map<string, { id: string; username: string; email: string }>();
        for (const allocation of response?.data?.allocations || []) {
          const recruiter = allocation?.assignedRecruiterId;
          const id = String(recruiter?._id || recruiter?.id || "");
          if (!id || byId.has(id)) continue;
          byId.set(id, {
            id,
            username: recruiter?.username || recruiter?.email || "User",
            email: recruiter?.email || "",
          });
        }
        setRecruiters(
          [...byId.values()].sort((a, b) => (a.username || "").localeCompare(b.username || ""))
        );
      } catch {
        setRecruiters([]);
      }
    })();
  }, [user?.role]);

  const downloadExcel = async () => {
    setExporting(true);
    toast.loading("Preparing Excel...", { id: "funnel-excel" });
    try {
      const exportPageSize = 5000;
      const filterParams = {
        search: filters.search || undefined,
        status: filters.status || undefined,
        manualStatus: filters.manualStatus || undefined,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        interest: filters.interest || undefined,
        source: filters.source || undefined,
        recruiterId: filters.recruiterId || undefined,
      };
      const first = await getHunarFunnelRecords({ ...filterParams, limit: exportPageSize, page: 1 });
      const allRecords: FunnelRecord[] = [...(first?.data?.records || [])];
      const pages = first?.totalPages || 1;
      for (let p = 2; p <= pages; p++) {
        toast.loading(`Exporting page ${p} of ${pages}...`, { id: "funnel-excel" });
        const next = await getHunarFunnelRecords({ ...filterParams, limit: exportPageSize, page: p });
        allRecords.push(...(next?.data?.records || []));
      }
      if (allRecords.length === 0) {
        toast.error("No records found for the selected filters.", { id: "funnel-excel" });
        return;
      }
      const worksheet = XLSX.utils.json_to_sheet(allRecords.map(toExcelRow));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "AI Funnel");
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `ai_funnel_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${allRecords.length} records.`, { id: "funnel-excel" });
    } catch {
      toast.error("Failed to download Excel.", { id: "funnel-excel" });
    } finally {
      setExporting(false);
    }
  };

  const isAdmin = user?.role === "ADMIN" || isAiFunnelOnlyUser(user);
  const outcomeKeys = OUTCOME_KEYS;
  const headers = [
    "Created",
    "Candidate",
    "Mobile",
    "Source",
    "Status",
    "Response time",
    "Sheet Job ID",
    "Duration",
    "Retry count",
    "Retries left",
    "Recording",
    ...outcomeKeys,
    "Recruiter",
    "Job",
    "Company",
    "Note",
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">AI Funnel</h1>
          <p className="text-sm text-gray-600">
            {isAdmin ? "Admin view for all AI funnel records." : "Assigned recruiter view for AI funnel records."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="date"
            className="border rounded-md px-3 py-2 text-sm"
            value={filters.fromDate}
            onChange={(e) => setFilters((prev) => ({ ...prev, fromDate: e.target.value }))}
            title="From date"
          />
          <input
            type="date"
            className="border rounded-md px-3 py-2 text-sm"
            value={filters.toDate}
            onChange={(e) => setFilters((prev) => ({ ...prev, toDate: e.target.value }))}
            title="To date"
          />
          {user?.role === "ADMIN" && (
            <button
              type="button"
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-md disabled:opacity-50 transition-colors"
              disabled={retriggering}
              onClick={async () => {
                const confirmed = window.confirm(
                  "Place a new Hunar call for every row Hunar cancelled? This starts only after you confirm."
                );
                if (!confirmed) return;
                setRetriggering(true);
                try {
                  await retriggerHunarCancelledCalls();
                } catch {
                  /* toast already shown */
                } finally {
                  setRetriggering(false);
                }
              }}
            >
              {retriggering ? "Queuing..." : "Retrigger Hunar cancelled"}
            </button>
          )}
          {user?.role === "ADMIN" && (
            <button
              type="button"
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md disabled:opacity-50 transition-colors"
              disabled={exporting}
              onClick={downloadExcel}
            >
              {exporting ? "Downloading..." : "Download Excel"}
            </button>
          )}
        </div>
      </div>

      {/* <form onSubmit={submitApiRecord} className="grid grid-cols-1 md:grid-cols-5 gap-3 bg-white p-4 rounded-lg border">
        <input
          className="border rounded-md px-3 py-2"
          placeholder="Candidate name"
          value={form.candidateName}
          onChange={(e) => setForm((prev) => ({ ...prev, candidateName: e.target.value }))}
          required
        />
        <input
          className="border rounded-md px-3 py-2"
          placeholder="Mobile number"
          value={form.mobileNumber}
          onChange={(e) => setForm((prev) => ({ ...prev, mobileNumber: e.target.value }))}
          required
        />
        <input
          className="border rounded-md px-3 py-2"
          placeholder="Email"
          value={form.email}
          onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
        />
        <input
          className="border rounded-md px-3 py-2"
          placeholder="Sheet Job ID"
          value={form.sheetJobId}
          onChange={(e) => setForm((prev) => ({ ...prev, sheetJobId: e.target.value }))}
        />
        <button className="px-4 py-2 bg-slate-900 text-white rounded-md" type="submit">
          Add API Record
        </button>
      </form> */}

      <div className={`grid grid-cols-1 gap-3 ${user?.role === "ADMIN" ? "md:grid-cols-6" : "md:grid-cols-5"}`}>
        <input
          className="border rounded-md px-3 py-2"
          placeholder="Search name/mobile/request ID"
          value={filters.search}
          onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
        />
        {user?.role === "ADMIN" && (
          <SearchableSelect
            options={[
              { value: "", label: "All recruiters" },
              ...recruiters.map((recruiter) => ({
                value: recruiter.id,
                label: recruiter.username || recruiter.email,
                searchText: `${recruiter.username} ${recruiter.email}`.trim(),
              })),
            ]}
            value={filters.recruiterId}
            onValueChange={(value) => setFilters((prev) => ({ ...prev, recruiterId: value }))}
            placeholder="All recruiters"
            className="w-full"
          />
        )}
        <select
          className="border rounded-md px-3 py-2"
          value={filters.source}
          onChange={(e) => setFilters((prev) => ({ ...prev, source: e.target.value }))}
        >
          <option value="">All sources</option>
          <option value="approach">Approaches</option>
          <option value="google_sheet">Sheet</option>
          <option value="api">API</option>
        </select>
        <select
          className="border rounded-md px-3 py-2"
          value={filters.status}
          onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
        >
          <option value="">All statuses</option>
          <option value="unmapped">Unmapped</option>
          <option value="pending_send">Pending send</option>
          <option value="awaiting_approach_check">Awaiting approach check</option>
          <option value="call_created">Call created</option>
          <option value="completed">Completed</option>
          <option value="not_connected">Not connected</option>
          <option value="failed">Failed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select
          className="border rounded-md px-3 py-2"
          value={filters.manualStatus}
          onChange={(e) => setFilters((prev) => ({ ...prev, manualStatus: e.target.value }))}
        >
          <option value="">All statuses</option>
          <option value="unmarked">Not marked</option>
          <option value="not_interested">Not Interested</option>
          <option value="interested">Interested</option>
          <option value="scheduled">Scheduled</option>
          <option value="not_reachable">Not Reachable</option>
          <option value="call_back">Call Back</option>
        </select>
        <select
          className="border rounded-md px-3 py-2"
          value={filters.interest}
          onChange={(e) => setFilters((prev) => ({ ...prev, interest: e.target.value }))}
        >
          <option value="">All interest</option>
          <option value="Interested">Interested</option>
          <option value="Not Interested">Not Interested</option>
          <option value="Unsure">Unsure</option>
          <option value="Not Answered">Not Answered</option>
          <option value="NOT AVAILABLE">Not Available</option>
        </select>
      </div>

      <div className="bg-white rounded-lg border overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {headers.map((header) => (
                <th key={header} className="text-left px-4 py-3 font-medium text-gray-700">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(8)].map((_, rowIndex) => (
                <tr key={`shimmer-${rowIndex}`} className="border-t">
                  {headers.map((header) => (
                    <td key={header} className="px-4 py-3">
                      <div
                        className={`animate-pulse bg-gray-200 rounded ${
                          header === "Status"
                            ? "h-8 w-36"
                            : header === "Recording"
                              ? "h-8 w-44"
                              : header === "Communication Feedback"
                                ? "h-4 w-56"
                                : "h-4 w-24"
                        }`}
                      />
                    </td>
                  ))}
                </tr>
              ))
            ) : records.length === 0 ? (
              <tr>
                <td className="px-4 py-6 text-gray-500" colSpan={headers.length}>No funnel records found.</td>
              </tr>
            ) : (
              records.map((record) => {
                const recruiter = record.recruiter || record.allocatedRecruiterId;
                const duration = formatDuration(record) || "-";
                return (
                  <tr key={record._id || record.id || record.requestId} className="border-t">
                    <td className="px-4 py-3 whitespace-nowrap">
                      {record.createdAt ? new Date(record.createdAt).toLocaleString("en-IN") : "-"}
                    </td>
                    <td className="px-4 py-3">{record.candidateName}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span>{record.mobileNumber}</span>
                        <ApproachWhatsAppChatPopover
                          iconOnly
                          candidateName={record.candidateName}
                          phone={record.mobileNumber}
                          loadConversation={async () => {
                            const recordId = record._id || record.id || record.requestId;
                            const response = await getHunarFunnelWhatsapp(recordId);
                            return {
                              whatsappChat: response?.data?.whatsappChat,
                              webhookEvents: response?.data?.whatsappWebhookEvents,
                            };
                          }}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {SOURCE_LABELS[record.source || ""] || record.source || "-"}
                    </td>
                    <td className="px-4 py-3 min-w-[180px]">
                      <select
                        className="border rounded px-2 py-1 text-sm w-full"
                        value={record.manualStatus || ""}
                        onChange={(e) => handleManualStatusChange(record, e.target.value)}
                      >
                        <option value="">—</option>
                        <option value="not_interested">Not Interested</option>
                        <option value="interested">Interested</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="not_reachable">Not Reachable</option>
                        <option value="call_back">Call Back</option>
                      </select>
                      {record.manualStatus && formatUpdatedAt(record.manualStatusUpdatedAt) ? (
                        <p className="mt-2 text-xs text-gray-500 whitespace-nowrap text-center">
                          {formatUpdatedAt(record.manualStatusUpdatedAt)}
                        </p>
                      ) : null}
                    </td>
                    <td
                      className={`px-4 py-3 whitespace-nowrap text-center ${responseTimeClass(
                        getTimeGapMinutes(record.interestUpdatedAt, record.manualStatusUpdatedAt)
                      )}`}
                    >
                      {formatTimeGap(record.interestUpdatedAt, record.manualStatusUpdatedAt) || "-"}
                    </td>
                    <td className="px-4 py-3">{record.sheetJobId || "-"}</td>
                    <td className="px-4 py-3">{duration}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {typeof record.retryCount === "number" && !Number.isNaN(record.retryCount)
                        ? record.retryCount
                        : "-"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {typeof record.retriesLeft === "number" && !Number.isNaN(record.retriesLeft)
                        ? record.retriesLeft
                        : "-"}
                    </td>
                    <td className="px-4 py-3 min-w-[260px]">
                      {record.recordingUrl ? (
                        <audio controls preload="none" className="w-full">
                          <source src={record.recordingUrl} />
                          Your browser does not support the audio element.
                        </audio>
                      ) : (
                        "-"
                      )}
                    </td>
                    {outcomeKeys.map((key) => (
                      <td key={key} className={`px-4 py-3 text-left ${key === "Communication Feedback" ? "min-w-[340px]" : "min-w-[220px]"}`}>
                        <div className="text-left">{record.result?.[key] || "-"}</div>
                        {key === "Interest" && record.result?.[key] && formatUpdatedAt(record.interestUpdatedAt) ? (
                          <p className="mt-2 text-xs text-gray-500 whitespace-nowrap text-left">
                            {formatUpdatedAt(record.interestUpdatedAt)}
                          </p>
                        ) : null}
                      </td>
                    ))}
                    <td className="px-4 py-3">{recruiter?.username || recruiter?.email || "-"}</td>
                    <td className="px-4 py-3 min-w-[300px]">
                      {record.internalJobId?.title || "-"}
                      {record.internalJobId?.location ? `, ${record.internalJobId.location}` : ""}
                    </td>
                    <td className="px-4 py-3 min-w-[180px]">{record.internalJobId?.companyName || "-"}</td>
                    <td className="px-4 py-3 min-w-[220px]">
                      {(record.note || record.manualStatusReason) ? (
                        <p className="text-xs text-gray-600 mb-2 line-clamp-2" title={record.note || record.manualStatusReason || ""}>{record.note || record.manualStatusReason}</p>
                      ) : null}
                      <button
                        className="px-3 py-1 text-xs border rounded-md whitespace-nowrap hover:bg-gray-50"
                        onClick={() => openNoteModal(record)}
                      >
                        {(record.note || record.manualStatusReason) ? "Edit Note" : "Add Note"}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalResults > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalResults)} of {totalResults} records
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-1.5 text-sm border rounded-md disabled:opacity-40"
                disabled={page <= 1}
                onClick={() => loadRecords(page - 1)}
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                .reduce<(number | "...")[]>((acc, p, idx, arr) => {
                  if (idx > 0 && p - (arr[idx - 1] ?? 0) > 1) acc.push("...");
                  acc.push(p);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === "..." ? (
                    <span key={`ellipsis-${idx}`} className="px-1 text-gray-400">…</span>
                  ) : (
                    <button
                      key={item}
                      className={`px-3 py-1.5 text-sm border rounded-md ${item === page ? "bg-slate-900 text-white" : ""}`}
                      onClick={() => loadRecords(item as number)}
                    >
                      {item}
                    </button>
                  )
                )}
              <button
                className="px-3 py-1.5 text-sm border rounded-md disabled:opacity-40"
                disabled={page >= totalPages}
                onClick={() => loadRecords(page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {reasonModal.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-3">Not Interested — Reason</h3>
            <textarea
              className="w-full border rounded-md px-3 py-2 text-sm"
              rows={3}
              placeholder="Enter reason for marking as not interested..."
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-4 py-2 text-sm border rounded-md"
                onClick={() => setReasonModal({ recordId: "", open: false })}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-md disabled:opacity-50"
                disabled={!reasonText.trim()}
                onClick={submitNotInterested}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      {noteModal.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-3">{noteText ? "Edit Note" : "Add Note"}</h3>
            <textarea
              className="w-full border rounded-md px-3 py-2 text-sm"
              rows={4}
              placeholder="Enter a note for this record..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-4 py-2 text-sm border rounded-md"
                onClick={() => setNoteModal({ recordId: "", open: false })}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm bg-slate-900 text-white rounded-md"
                onClick={submitNote}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HunarFunnelPage;
